import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

// 公共 API

// 獲取候位狀態
const getQueueStatus = async () => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.QUEUE}/status`);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('獲取候位狀態錯誤:', error);
    throw error;
  }
};

// 登記候位
const registerQueue = async (queueData) => {
  try {
    const response = await axios.post(`${API_ENDPOINTS.QUEUE}/register`, queueData);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('登記候位錯誤:', error);
    throw error;
  }
};

// 獲取特定候位號碼的狀態
const getQueueNumberStatus = async (queueNumber) => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.QUEUE}/number/${queueNumber}`);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('獲取候位號碼狀態錯誤:', error);
    throw error;
  }
};

// 通過姓名或電話查詢候位號碼（支持單一條件查詢）
const searchQueueByNameOrPhone = async (name, phone) => {
  try {
    // 構建查詢參數，過濾空值
    const params = {};
    if (name && name.trim()) {
      params.name = name.trim();
    }
    if (phone && phone.trim()) {
      params.phone = phone.trim();
    }
    
    const response = await axios.get(`${API_ENDPOINTS.QUEUE}/search`, {
      params
    });
    
    // v1 API 回應格式：{success, code, message, data}
    
    // 檢查後端實際返回的數據格式
    if (response.data.success) {
      let result;
      
      // 檢查 data 是陣列還是物件
      if (Array.isArray(response.data.data)) {
        // data 是陣列：直接使用
        result = {
          records: response.data.data,
          message: response.data.message || '查詢完成'
        };
      } else if (response.data.data && typeof response.data.data === 'object') {
        // data 是物件：檢查是否有 records 屬性
        if (response.data.data.records && Array.isArray(response.data.data.records)) {
          result = {
            records: response.data.data.records,
            message: response.data.data.message || response.data.message || '查詢完成'
          };
        } else {
          // 檢查是否是類似陣列的物件 (有數字索引的物件)
          const keys = Object.keys(response.data.data);
          const isArrayLikeObject = keys.every(key => /^\d+$/.test(key));
          
          if (isArrayLikeObject && keys.length > 0) {
            // 將類似陣列的物件轉換為真正的陣列
            const recordsArray = keys
              .map(key => parseInt(key))
              .sort((a, b) => a - b)
              .map(index => response.data.data[index]);
            
            result = {
              records: recordsArray,
              message: response.data.message || '查詢完成'
            };
          } else {
            // data 是一般物件：當作單筆記錄
            result = {
              records: [response.data.data],
              message: response.data.message || '查詢完成'
            };
          }
        }
      } else {
        // data 為空或其他格式
        result = {
          records: [],
          message: response.data.message || '查詢完成'
        };
      }
      
      return result;
    } else {
      throw new Error(response.data.message || '查詢失敗');
    }
  } catch (error) {
    console.error('查詢候位號碼錯誤:', error);
    throw error;
  }
};

// 為了向後兼容，保留原函數名的別名
const searchQueueByNameAndPhone = searchQueueByNameOrPhone;

// 獲取排序的候位號碼（公共API）
const getPublicOrderedNumbers = async () => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.QUEUE}/ordered-numbers`);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('獲取公共排序候位號碼錯誤:', error);
    throw error.response?.data || error;
  }
};

// 獲取最大叫號順序（公共API）
const getMaxOrderIndex = async () => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.QUEUE}/max-order`);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('獲取最大叫號順序錯誤:', error);
    throw error.response?.data || error;
  }
};

// 獲取順序1和順序2的客戶號碼
const getOrderedQueueNumbers = async (token) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  try {
    const response = await axios.get(`${API_ENDPOINTS.ADMIN}/queue/ordered-numbers`, config);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('獲取順序客戶號碼錯誤:', error);
    throw error.response?.data || error;
  }
};

// 管理員 API

// 獲取候位列表
const getQueueList = async (params = {}, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params
    };
    const response = await axios.get(`${API_ENDPOINTS.ADMIN}/queue/list`, config);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('獲取候位列表錯誤:', error);
    throw error;
  }
};

// 呼叫下一位
const callNextQueue = async (token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(`${API_ENDPOINTS.ADMIN}/queue/next`, {}, config);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('呼叫下一位錯誤:', error);
    throw error;
  }
};

// 更新候位狀態
const updateQueueStatus = async (queueId, status, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(
      `${API_ENDPOINTS.ADMIN}/queue/${queueId}/status`,
      { status },
      config
    );
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('更新候位狀態錯誤:', error);
    throw error;
  }
};

// 批量重排序（方案 B）
const reorderQueue = async (orderedIds, token) => {
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const response = await axios.put(
    `${API_ENDPOINTS.ADMIN}/queue/reorder`,
    { orderedIds },
    config
  );
  if (!response.data?.success) throw new Error('批量排序 API 回應格式不正確');
  return response.data.data;
};

// 更新候位順序
const updateQueueOrder = async (queueId, newOrder, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(
      `${API_ENDPOINTS.ADMIN}/queue/order`,
      { queueId, newOrder },
      config
    );
    
    // 檢查回應格式
    if (!response.data || !response.data.success) {
      console.error('API回應格式不正確:', response.data);
      throw new Error('API回應格式不正確');
    }
    
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('更新順序服務錯誤:', error);
    throw error.response?.data || error;
  }
};

// 設置下次辦事時間
const setNextSessionDate = async (nextSessionDate, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(
      `${API_ENDPOINTS.ADMIN}/settings/next-session`,
      { nextSessionDate },
      config
    );
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('設置下次辦事時間錯誤:', error);
    throw error;
  }
};

// 開關候位功能
const toggleQueueStatus = async (isOpen, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(
      `${API_ENDPOINTS.ADMIN}/settings/queue-status`,
      { isOpen },
      config
    );
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('開關候位功能錯誤:', error);
    throw error;
  }
};

// 設定最大叫號順序上限
const setMaxOrderIndex = async (maxOrderIndex, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(
      `${API_ENDPOINTS.ADMIN}/settings/max-order-index`,
      { maxOrderIndex },
      config
    );
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('設定最大叫號順序上限錯誤:', error);
    throw error;
  }
};

// 更新客戶資料
const updateQueueData = async (queueId, customerData, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(
      `${API_ENDPOINTS.ADMIN}/queue/${queueId}/update`,
      customerData,
      config
    );
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('更新客戶資料錯誤:', error);
    throw error;
  }
};

// 刪除客戶資料
const deleteCustomer = async (queueId, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.delete(`${API_ENDPOINTS.ADMIN}/queue/${queueId}/delete`, config);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('刪除客戶資料錯誤:', error);
    throw error;
  }
};

// 設定每位客戶預估處理時間
const setMinutesPerCustomer = async (minutesPerCustomer, token) => {
  const response = await axios.put(
    `${API_ENDPOINTS.ADMIN}/settings/minutes-per-customer`,
    { minutesPerCustomer },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  // v1 API 回應格式：{success, code, message, data}，回傳實際數據
  return response.data.data || response.data;
    };

// 設定簡化模式
const setSimplifiedMode = async (simplifiedMode, token) => {
    const response = await axios.put(
    `${API_ENDPOINTS.ADMIN}/settings/simplified-mode`,
    { simplifiedMode },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
    );
  // v1 API 回應格式：{success, code, message, data}，回傳實際數據
  return response.data.data || response.data;
};

// 設定公開候位登記功能
const setPublicRegistrationEnabled = async (publicRegistrationEnabled, token) => {
    const response = await axios.put(
    `${API_ENDPOINTS.ADMIN}/settings/public-registration-enabled`,
    { publicRegistrationEnabled },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
    );
  // v1 API 回應格式：{success, code, message, data}，回傳實際數據
  return response.data.data || response.data;
};

// 設定查詢頁號碼顯示開關
const setShowQueueNumberInQuery = async (showQueueNumberInQuery, token) => {
    const response = await axios.put(
    `${API_ENDPOINTS.ADMIN}/settings/show-queue-number-in-query`,
    { showQueueNumberInQuery },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
    );
  return response.data.data || response.data;
};

// 獲取活動報名區塊設定
const getEventBanner = async () => {
  const response = await axios.get(
    `${API_ENDPOINTS.ADMIN}/settings/event-banner`
  );
  // v1 API 回應格式：{success, code, message, data}，回傳實際數據
  return response.data.data || response.data;
};

// 更新活動報名區塊設定
const updateEventBanner = async (data, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(
      `${API_ENDPOINTS.ADMIN}/settings/event-banner`,
      data,
      config
    );
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('更新活動報名設定錯誤:', error);
    throw error;
  }
};

// 獲取下次開科辦事開放報名時間設定
const getScheduledOpenTime = async () => {
  try {
    const response = await axios.get(
      `${API_ENDPOINTS.ADMIN}/settings/scheduled-open-time`
    );
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('獲取開放報名時間設定錯誤:', error);
    throw error.response?.data?.message || '獲取開放報名時間設定失敗';
  }
};

// 設定下次開科辦事開放報名時間
const setScheduledOpenTime = async (scheduledOpenTime, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(
      `${API_ENDPOINTS.ADMIN}/settings/scheduled-open-time`,
      { scheduledOpenTime },
      config
    );
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('更新開放報名時間設定錯誤:', error);
    throw error.response?.data?.message || '更新開放報名時間設定失敗';
  }
};

// 設定定時開放開關
const setAutoOpenEnabled = async (autoOpenEnabled, token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.put(
      `${API_ENDPOINTS.ADMIN}/settings/auto-open-enabled`,
      { autoOpenEnabled },
      config
    );
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('設定定時開放錯誤:', error);
    throw error.response?.data?.message || '設定定時開放失敗';
  }
};

// 設定客戶總數
const setTotalCustomerCount = async (totalCustomerCount, token) => {
  const response = await axios.put(
    `${API_ENDPOINTS.ADMIN}/settings/total-customer-count`,
    { totalCustomerCount },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  // v1 API 回應格式：{success, code, message, data}，回傳實際數據
  return response.data.data || response.data;
};

// 重設客戶總數
const resetTotalCustomerCount = async (token) => {
  const response = await axios.post(
    `${API_ENDPOINTS.ADMIN}/settings/reset-customer-count`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  // v1 API 回應格式：{success, code, message, data}，回傳實際數據
  return response.data.data || response.data;
};

// 設定上一位辦完時間
const setLastCompletedTime = async (lastCompletedTime, token) => {
  const response = await axios.put(
    `${API_ENDPOINTS.ADMIN}/settings/last-completed-time`,
    { lastCompletedTime },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  // v1 API 回應格式：{success, code, message, data}，回傳實際數據
  return response.data.data || response.data;
};

// 重設上一位辦完時間
const resetLastCompletedTime = async (token) => {
  const response = await axios.post(
    `${API_ENDPOINTS.ADMIN}/settings/reset-completed-time`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  // v1 API 回應格式：{success, code, message, data}，回傳實際數據
  return response.data.data || response.data;
};

// 結束本期（歸檔 + 清空）
const endSession = async (token) => {
  try {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await axios.post(`${API_ENDPOINTS.ADMIN}/queue/end-session`, {}, config);
    return response.data.data || response.data;
  } catch (error) {
    console.error('結束本期錯誤:', error);
    throw error;
  }
};

// 清空所有候位
const clearAllQueue = async (token) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.delete(`${API_ENDPOINTS.ADMIN}/queue/clear-all`, config);
    // v1 API 回應格式：{success, code, message, data}，回傳實際數據
    return response.data.data || response.data;
  } catch (error) {
    console.error('清空候位錯誤:', error);
    throw error;
  }
};

const queueService = {
  // 公共 API
  getQueueStatus,
  registerQueue,
  getQueueNumberStatus,
  searchQueueByNameAndPhone,
  searchQueueByNameOrPhone,
  getPublicOrderedNumbers,
  getMaxOrderIndex,
  
  // 管理員 API
  getQueueList,
  callNextQueue,
  updateQueueStatus,
  updateQueueOrder,
  reorderQueue,
  setNextSessionDate,
  toggleQueueStatus,
  setMaxOrderIndex,
  updateQueueData,
  deleteCustomer,
  setMinutesPerCustomer,
  setSimplifiedMode,
  setPublicRegistrationEnabled,
  setShowQueueNumberInQuery,
  getEventBanner,
  updateEventBanner,
  getScheduledOpenTime,
  setScheduledOpenTime,
  setAutoOpenEnabled,
  setTotalCustomerCount,
  resetTotalCustomerCount,
  setLastCompletedTime,
  resetLastCompletedTime,
  clearAllQueue,
  endSession,
  getOrderedQueueNumbers
};

export default queueService; 